import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  applyPosStructureDraft,
  ensurePosStructureLayout,
  extractPosStructureDraft,
  getPosSectionMaxWidthMm,
  getSectionWidthMm,
  normalizePosStructureDraft,
} from '@/components/template-builder/posStructureConfig';

const cloneDraft = (draft) => JSON.parse(JSON.stringify(draft));

const updateSection = (draft, sectionKey, patch) => ({
  ...draft,
  [sectionKey]: {
    ...draft[sectionKey],
    ...patch,
  },
});

const updateColumn = (draft, sectionKey, columnIndex, patch) => ({
  ...draft,
  [sectionKey]: {
    ...draft[sectionKey],
    columns: draft[sectionKey].columns.map((column, index) => (
      index === columnIndex ? { ...column, ...patch } : column
    )),
  },
});

const numericValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const SECTION_SAFE_WIDTH_MM = 72;

const ULTRA_COMPACT_PRESET = {
  items: {
    xMm: 2.8,
    titleYmm: 39.5,
    tableYmm: 42.6,
    tableHeightMm: 38.5,
    fontSize: 5.2,
    widths: {
      description: 34,
      unit_price: 12.5,
      qty: 8,
      line_total: 18,
    },
  },
  tax: {
    xMm: 2.8,
    titleYmm: 83.2,
    tableYmm: 86.2,
    tableHeightMm: 12.5,
    fontSize: 5.4,
    widths: {
      rate: 9,
      base: 19,
      vat: 13.5,
      total: 17.5,
    },
  },
};

const applyUltraCompactPreset = (draft) => {
  const nextDraft = cloneDraft(draft);

  return normalizePosStructureDraft(Object.entries(ULTRA_COMPACT_PRESET).reduce((accumulator, [sectionKey, preset]) => ({
    ...accumulator,
    [sectionKey]: {
      ...accumulator[sectionKey],
      xMm: preset.xMm,
      titleYmm: preset.titleYmm,
      tableYmm: preset.tableYmm,
      tableHeightMm: preset.tableHeightMm,
      fontSize: preset.fontSize,
      columns: (accumulator[sectionKey]?.columns || []).map((column) => ({
        ...column,
        widthMm: preset.widths[column.field] ?? column.widthMm,
      })),
    },
  }), nextDraft));
};

const SectionControls = ({ draft, sectionKey, title, onDraftChange, isFirst = false }) => {
  const section = draft[sectionKey];
  const totalWidth = useMemo(() => getSectionWidthMm(section), [section]);
  const maxWidthMm = useMemo(() => getPosSectionMaxWidthMm(sectionKey, section?.xMm), [section?.xMm, sectionKey]);
  const widthFitsThermal = totalWidth <= maxWidthMm;

  return (
    <div
      className={`space-y-2 px-3 py-3 ${isFirst ? '' : 'border-t border-slate-200'}`}
      data-testid={`pos-structure-${sectionKey}-card`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold leading-tight">{title}</h4>
          <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`pos-structure-${sectionKey}-section-note`}>
            Ajuste fino em milímetros, já no formato compacto do talão curto.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`pos-structure-${sectionKey}-summary-badges`}>
          <Badge variant="secondary" className="px-2 py-0.5 text-[10px]">
            Largura {totalWidth.toFixed(1)} mm
          </Badge>
          <Badge variant="secondary" className="px-2 py-0.5 text-[10px]">
            Máx. papel {maxWidthMm.toFixed(1)} mm
          </Badge>
          <Badge variant="secondary" className="px-2 py-0.5 text-[10px]">
            Fonte {Number(section.fontSize || 0).toFixed(1)}
          </Badge>
          <Badge className={`px-2 py-0.5 text-[10px] ${widthFitsThermal ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
            {widthFitsThermal ? 'Dentro do talão' : 'Rever largura'}
          </Badge>
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid gap-2 md:grid-cols-5" data-testid={`pos-structure-${sectionKey}-metrics-grid`}>
          <div>
            <Label className="text-xs text-muted-foreground" htmlFor={`${sectionKey}-x-input`}>Posição X (mm)</Label>
            <Input
              id={`${sectionKey}-x-input`}
              type="number"
              step="0.5"
              value={section.xMm}
              onChange={(event) => onDraftChange(updateSection(draft, sectionKey, { xMm: numericValue(event.target.value, section.xMm) }))}
              data-testid={`pos-structure-${sectionKey}-x-input`}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground" htmlFor={`${sectionKey}-title-y-input`}>Título Y (mm)</Label>
            <Input
              id={`${sectionKey}-title-y-input`}
              type="number"
              step="0.5"
              value={section.titleYmm}
              onChange={(event) => onDraftChange(updateSection(draft, sectionKey, { titleYmm: numericValue(event.target.value, section.titleYmm) }))}
              data-testid={`pos-structure-${sectionKey}-title-y-input`}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground" htmlFor={`${sectionKey}-table-y-input`}>Tabela Y (mm)</Label>
            <Input
              id={`${sectionKey}-table-y-input`}
              type="number"
              step="0.5"
              value={section.tableYmm}
              onChange={(event) => onDraftChange(updateSection(draft, sectionKey, { tableYmm: numericValue(event.target.value, section.tableYmm) }))}
              data-testid={`pos-structure-${sectionKey}-table-y-input`}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground" htmlFor={`${sectionKey}-height-input`}>Altura da tabela (mm)</Label>
            <Input
              id={`${sectionKey}-height-input`}
              type="number"
              step="0.5"
              value={section.tableHeightMm}
              onChange={(event) => onDraftChange(updateSection(draft, sectionKey, { tableHeightMm: numericValue(event.target.value, section.tableHeightMm) }))}
              data-testid={`pos-structure-${sectionKey}-height-input`}
            />
          </div>
          <div className="rounded-lg border bg-muted/30 px-2.5 py-2" data-testid={`pos-structure-${sectionKey}-width-summary`}>
            <Label className="text-[11px] text-muted-foreground">Largura total</Label>
            <p className="mt-1 text-sm font-semibold leading-none">{totalWidth.toFixed(2)} mm</p>
          </div>
        </div>

        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_200px]" data-testid={`pos-structure-${sectionKey}-secondary-grid`}>
          <div>
            <Label className="text-xs text-muted-foreground" htmlFor={`${sectionKey}-font-size-input`}>Tamanho da fonte</Label>
            <Input
              id={`${sectionKey}-font-size-input`}
              type="number"
              step="0.5"
              value={section.fontSize}
              onChange={(event) => onDraftChange(updateSection(draft, sectionKey, { fontSize: numericValue(event.target.value, section.fontSize) }))}
              data-testid={`pos-structure-${sectionKey}-font-size-input`}
            />
          </div>
          <div className="rounded-lg border bg-slate-50 px-3 py-2" data-testid={`pos-structure-${sectionKey}-fit-summary`}>
            <Label className="text-[11px] text-muted-foreground">Diagnóstico rápido</Label>
            <p className="mt-1 text-xs font-medium leading-relaxed text-slate-700">
              {widthFitsThermal
                ? 'A secção cabe dentro da largura útil do papel POS.'
                : 'O editor auto-ajustou a estrutura para não ultrapassar o papel POS.'}
            </p>
          </div>
        </div>

        <div className="space-y-2" data-testid={`pos-structure-${sectionKey}-columns-panel`}>
          <div
            className="hidden items-center gap-2 rounded-lg border border-dashed bg-slate-50 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground md:grid md:grid-cols-[44px_minmax(0,1.35fr)_110px_140px]"
            data-testid={`pos-structure-${sectionKey}-columns-header`}
          >
            <span>#</span>
            <span>Coluna</span>
            <span>Largura (mm)</span>
            <span>Alinhamento</span>
          </div>
          {(section.columns || []).map((column, columnIndex) => (
            <div
              key={`${sectionKey}-${column.field}`}
              className="grid items-end gap-2 rounded-lg border px-2.5 py-2 md:grid-cols-[44px_minmax(0,1.35fr)_110px_140px]"
              data-testid={`pos-structure-${sectionKey}-column-${column.field}`}
            >
              <div className="rounded-md bg-muted/50 px-2 py-2 text-center text-[11px] font-semibold text-slate-600">
                {columnIndex + 1}
              </div>
              <div>
                <p className="text-sm font-medium" data-testid={`pos-structure-${sectionKey}-column-label-${column.field}`}>{column.label}</p>
                <p className="text-[11px] text-muted-foreground" data-testid={`pos-structure-${sectionKey}-column-field-${column.field}`}>{column.field}</p>
              </div>
              <div>
                <Input
                  id={`${sectionKey}-${column.field}-width-input`}
                  type="number"
                  step="0.5"
                  value={column.widthMm}
                  onChange={(event) => onDraftChange(updateColumn(draft, sectionKey, columnIndex, { widthMm: numericValue(event.target.value, column.widthMm) }))}
                  aria-label={`Largura da coluna ${column.label} em milímetros`}
                  placeholder="mm"
                  data-testid={`pos-structure-${sectionKey}-column-width-${column.field}`}
                />
              </div>
              <div>
                <Select
                  value={column.align}
                  onValueChange={(value) => onDraftChange(updateColumn(draft, sectionKey, columnIndex, { align: value }))}
                >
                  <SelectTrigger data-testid={`pos-structure-${sectionKey}-column-align-${column.field}`}>
                    <SelectValue placeholder="Alinhamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Esquerda</SelectItem>
                    <SelectItem value="center">Centro</SelectItem>
                    <SelectItem value="right">Direita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const PosStructureEditor = ({ templateId, layout, onSave, onPreview }) => {
  const [draft, setDraft] = useState(() => normalizePosStructureDraft(extractPosStructureDraft(layout)));
  const normalizedLayout = useMemo(() => ensurePosStructureLayout(layout), [layout]);

  useEffect(() => {
    setDraft(normalizePosStructureDraft(cloneDraft(extractPosStructureDraft(layout))));
  }, [layout, templateId]);

  const applyDraftUpdate = (nextDraft) => {
    setDraft((currentDraft) => {
      const resolvedDraft = typeof nextDraft === 'function' ? nextDraft(currentDraft) : nextDraft;
      return normalizePosStructureDraft(resolvedDraft);
    });
  };

  const previewLayout = useMemo(() => applyPosStructureDraft(normalizedLayout, draft), [draft, normalizedLayout]);

  return (
    <div className="space-y-3" data-testid="pos-structure-editor">
      <div className="rounded-xl border bg-amber-50/80 p-3" data-testid="pos-structure-editor-banner">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-amber-950">Editor estrutural do talão curto</p>
            <p className="mt-1 text-xs text-amber-900" data-testid="pos-structure-editor-banner-text">
              Um único template global por tipo de documento. Os dados da loja são injectados dinamicamente pela sessão activa no momento da impressão.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="pos-structure-editor-banner-badges">
            <Badge variant="secondary" className="bg-white/80 text-[10px] text-amber-950">Modo compacto</Badge>
            <Badge variant="secondary" className="bg-white/80 text-[10px] text-amber-950">Milímetros verificáveis</Badge>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border bg-white" data-testid="pos-structure-editor-sections-list">
        <SectionControls draft={draft} sectionKey="items" title="Tabela Itens / Serviços" onDraftChange={applyDraftUpdate} isFirst />
        <SectionControls draft={draft} sectionKey="tax" title="Quadro Resumo de Impostos" onDraftChange={applyDraftUpdate} />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="pos-structure-editor-actions">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => applyDraftUpdate((currentDraft) => applyUltraCompactPreset(currentDraft))}
          data-testid="pos-structure-ultra-compact-btn"
        >
          Aplicar ultra compacto
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => applyDraftUpdate(cloneDraft(extractPosStructureDraft(null)))}
          data-testid="pos-structure-reset-btn"
        >
          Reorganizar do zero
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPreview(previewLayout)}
          data-testid="pos-structure-preview-btn"
        >
          Pré-visualizar estrutura
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => onSave(previewLayout)}
          data-testid="pos-structure-save-btn"
        >
          Guardar estrutura POS
        </Button>
      </div>
    </div>
  );
};